import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModelCommentEntity } from '../../domain';
import { ModelCommentHeader } from './ModelCommentHeader';
import { ModelCommentBodyForm } from '../ModelCommentBodyForm';
import { Button } from '@framework/ui/atoms/Button';
import type { ModelCommentThreadOperation } from '../../adapter';
import { useUserPublicProfile } from '@user/PublicProfile/useUserPublicProfile';
import type { Timestamp } from '@framework/Timestamp';
import { ModelCommentBody } from './ModelCommentBody';
import { formatRelativeTime } from '@framework/i18n/formatRelativeTime';
import { MODEL_COMMENT_RELATIVE_TIME_I18N_KEYS } from './modelCommentRelativeTimeI18nKeys';

type Props = {
  comment: ModelCommentEntity;
  now: Timestamp;
  isEditing: boolean;
  threadOperation: ModelCommentThreadOperation;
  onSubmit(): void;
  onCancel(): void;
};

export const ModelCommentItem: React.FC<Props> = ({
  comment,
  now,
  isEditing,
  threadOperation,
  onSubmit,
  onCancel,
}: Props) => {
  const { t } = useTranslation();
  const [body, setBody] = useState<string>('');
  const profile = useUserPublicProfile(comment.userId);

  useEffect(() => {
    setBody(comment.getBody());
  }, [comment, isEditing]);

  const handleChange = useCallback((newBody: string) => {
    setBody(newBody);
  }, []);

  const handleEditSubmit = useCallback(() => {
    threadOperation.updateCommentBody(comment.id, body).then();
    onSubmit();
  }, [body, comment.id, onSubmit, threadOperation]);

  const handleEditCancel = useCallback(() => {
    setBody(comment.getBody());
    onCancel();
  }, [onCancel, comment]);

  // profile 未取得段階では非表示
  if (!profile) return null;

  return (
    <>
      <ModelCommentHeader
        author={profile}
        postAt={formatRelativeTime(comment.getPostAt(), now, t, MODEL_COMMENT_RELATIVE_TIME_I18N_KEYS)}
      />
      {isEditing ? (
        <>
          <ModelCommentBodyForm
            autoFocus={true}
            body={body}
            ariaLabel={t('canvas.comment.menu.editComment')}
            onChange={handleChange}
            onSubmit={handleEditSubmit}
            minRows={4}
          />
          <div className="flex justify-end gap-2">
            <Button color="mono" className="rounded-lg p-2 text-3xl" onClick={handleEditCancel}>
              {t('common.action.cancel')}
            </Button>
            <Button color="brand" className="rounded-lg p-2 text-3xl" onClick={handleEditSubmit} disabled={!body}>
              {t('common.action.save')}
            </Button>
          </div>
        </>
      ) : (
        <ModelCommentBody body={comment.getBody()} />
      )}
    </>
  );
};
